/* eslint-disable @typescript-eslint/no-explicit-any */
import './assets/tailwind.css';

import { ApolloClient, HttpLink, InMemoryCache, Observable } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';
import { configureAppEnv } from '@pbk-web/app-env';
import { PbkHeaders } from '@pbk-web/data';
import { configureImageLoader, ImageCacheProvider } from '@pbk-web/state';
import { FullscreenModal, Loader, ToastProvider, withSlot } from '@pbk-web/ui';
import { convertToRichText } from '@pbk-web/utils';
import { browserTracingIntegration, replayIntegration } from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import type { FC, ReactElement } from 'react';
import { Fragment, lazy, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import ErrorPage from './components/error/ErrorPage';
import UserListRenderer from './components/ownership/UserListRenderer';
import { FileUploadType } from './components/shared/file-upload/FileuploadType';
import { BannerProvider } from './contexts/BannerContext';
import { DebugHotkeyProvider } from './contexts/DebugHotkeyContext';
import { useFormAction } from './contexts/FormActionContext';
import { FormRendererType, useFormRendererInfo } from './contexts/FormRendererContext';
import { FormRendererDesignContext, FormRendererDesignDefaults } from './contexts/FormRendererDesignContext';
import { FormRendererMode } from './contexts/FormRendererDesignContextTypes';
import { LatestNotificationsProvider } from './contexts/LatestNotificationsContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { LexicalIntegrationsRoot } from './contexts/LexicalIntegrationsContext';
import { PbkAuth } from './contexts/PbkAuthContext';
import { useProcessesHub } from './contexts/signalR/ProcessesContext';
import { useCurrentUser } from './global-state/Auth';
import usePermissions from './hooks/permissions/usePermissions';
import { useFetchActiveClientGroups } from './hooks/useFetchActiveClientGroups';
import { useFetchActivitiesGQL } from './hooks/useFetchActivitiesGQL';
import useFetchClasses from './hooks/useFetchClasses';
import useFetchClientTags from './hooks/useFetchClientTags';
import useFetchClientTemplateForms from './hooks/useFetchClientTemplateForms';
import useFetchClientUsers from './hooks/useFetchClientUsers';
import { useFetchFormTemplatesConfiguration } from './hooks/useFetchFormTemplatesConfiguration';
import { useFetchModules } from './hooks/useFetchModules';
import { useStoredFilters } from './hooks/useStoredFilters';
import { useWarnOutboundLink } from './hooks/useWarnOutboundLink';
import i18n from './i18n';
import PbkErrorBoundary from './PbkErrorBoundry';
import ClientFormService from './services/ClientFormService';
import ClientTemplateFormService from './services/ClientTemplateFormService';
import ClientTemplateModuleService from './services/ClientTemplateModuleService';
import FileService from './services/FileService';
import ModuleService from './services/ModuleService';
import { FileUtils } from './utils/FileUtils';
import FormUtils from './utils/FormUtils';
import { lazyComponent } from './utils/lazyComponent';
import { clientFormRoute, goToClientForm } from './utils/NavigationUtils';

// Injected components load on first render so they stay off the startup path
const lazyLoader = (
  <div className="flex justify-center p-4">
    <Loader size={16} centered={false} />
  </div>
);
const FormRendererV2 = lazyComponent(() => import('./components/form/renderer/FormRendererV2'), lazyLoader);
const Groups = lazyComponent(() => import('./components/ownership/ClientFormRoleGroup').then((m) => ({ default: m.Groups })));
const FormsV2InviteMemberAdapter = lazyComponent(() =>
  import('./components/ownership/FormsV2InviteMemberAdapter').then((m) => ({ default: m.FormsV2InviteMemberAdapter })),
);
const UsersSelect = lazyComponent(() => import('./components/ownership/UsersSelect'));
const DataTable = withSlot(lazyComponent(() => import('./components/shared/data-grid/DataTable'), lazyLoader) as any);
const MultiFileInfo = lazyComponent(() => import('./components/shared/file-upload/MultiFileInfo'));
const UploadArea = lazyComponent(() => import('./components/shared/file-upload/UploadArea'), lazyLoader);
const InlineEditor = lazyComponent(() => import('./components/shared/form-control/InlineEditor'));
const DateFilterBase = lazyComponent(() => import('./components/table-view/filter-renderers/DateFilterBase'));
const HtmlPreview = lazyComponent(() => import('./components/shared/placeholder/HtmlPreview'));
const StaticBreadCrumb = lazyComponent(() => import('./components/shared/breadcumb/StaticBreadCrumb'));

configureImageLoader((fileId) => FileService.getFile(fileId));

configureAppEnv({
  DataTable,
  useFetchModules,
  useFetchActivitiesGQL,
  useFetchClientUsers,
  StaticBreadCrumb,
  useFetchFormTemplatesConfiguration,
  useFetchClientTemplateForms,
  useCurrentUser,
  useFetchClientGroups: useFetchActiveClientGroups,
  usePermissions,
  InlineEditor,
  ClientFormService,
  ClientTemplateFormService,
  FullscreenModal,
  UsersSelect,
  UserListRenderer,
  InviteMemberModal: FormsV2InviteMemberAdapter,
  Groups,
  useFetchClasses,
  ClientTemplateModuleService,
  ModuleService,
  UploadArea,
  MultiFileInfo,
  FileService,
  FileUtils,
  FileUploadType,
  FormRendererV2,
  FormRendererMode,
  FormRendererType,
  ErrorPage,
  DateFilterBase,
  useStoredFilters,
  useWarnOutboundLink,
  FormUtils,
  FormRendererDesignContext,
  FormRendererDesignDefaults,
  useFormAction,
  useFormRendererInfo,
  goToClientForm,
  clientFormRoute,
  useFetchClientTags,
  convertToRichText,
  HtmlPreview,
  useProcessesHub,
  FormsV2BuilderRealtimeProvider: lazy(() => import('./contexts/signalR/FormsV2BuilderRealtimeContext')),
  FormsV2FileServiceProvider: lazy(() => import('./components/forms-v2/FormsV2AppFileServiceProvider')),
});

const App = lazy(() => import('./App'));
import('./chartjsPlugins.js');

// The current deploy environment, sent to PostHog as a flag evaluation context so flags can be
// gated per environment from the PostHog UI (e.g. keep `playbook-forms-v2` off in production and
// staging). Only flags that declare evaluation contexts are constrained; flags without contexts
// (e.g. `playbook-labs`) evaluate as normal.
// https://posthog.com/docs/feature-flags/evaluation-contexts
const POSTHOG_EVALUATION_CONTEXT = (import.meta.env.VITE_ENVIRONMENT as string) || 'dev';

// `evaluation_contexts` is read at runtime by posthog-js but isn't in its typed config yet, so we
// widen the option type rather than casting to `any`.
const postHogOptions: Partial<PostHogConfig> & { evaluation_contexts?: string[] } = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  evaluation_contexts: [POSTHOG_EVALUATION_CONTEXT],
  session_recording: {
    maskAllInputs: true,
    maskInputFn: (text) => {
      return '*'.repeat(text.length);
    },
    maskTextSelector: '*',
    maskTextFn(text: string) {
      return '*'.repeat(text.length);
    },
  },
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT as string,
    release: import.meta.env.VITE_VERSION,
    beforeSend(event, hint) {
      const error = hint.originalException as any;
      // Check if error is an Axios error and has a response with status 404, 403, or 401
      if (error && [404, 403, 401].includes(error.status)) {
        return null;
      }
      if (event.user) {
        delete event.user.email;
      }
      if (event.extra?.dialog) {
        Sentry.showReportDialog({
          eventId: event.event_id,
        });
      }
      return event;
    },
    integrations: [
      replayIntegration(),
      Sentry.browserSessionIntegration(),
      browserTracingIntegration({
        instrumentNavigation: true,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({ useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes }),
    ],
    attachStacktrace: true,
    sampleRate: 1.0,
    debug: !!import.meta.hot,
    normalizeDepth: 10,
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^([a-z]+\.)?api\.([a-z]+\.)?playbk.io/, 'localhost:5000'],

    // If there are errors that do not originate from our app, consider decluttering
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
    denyUrls: [],
    allowUrls: [],
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      'CanceledError',
      // GraphQL cancelled error from Apollo Client
      'The operation was aborted',
      // Errors from within Froala editor
      "Cannot read properties of null (reading 'tagName')",
      'can\'t access property "on", this.editor.events is undefined',
      "Cannot read properties of undefined (reading 'on')",
    ],

    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  });
}

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '*',
    Component: () => (
      <PbkAuth>
        <LatestNotificationsProvider>
          <App />
        </LatestNotificationsProvider>
      </PbkAuth>
    ),
    ErrorBoundary: PbkErrorBoundary,
  },
]);

export const gqlState = {
  clientId: '',
  tenantId: '',
  getRefreshedToken: () => Promise.resolve(),
};

const apolloHttpLink = new HttpLink({
  uri: import.meta.env.VITE_API_BASE_URL + '/graphql',
  credentials: 'include',
});

const apolloAuthLink = new SetContextLink((prevContext) => {
  return {
    ...prevContext,
    headers: {
      ...prevContext.headers,
      [PbkHeaders.ClientId]: gqlState.clientId,
      [PbkHeaders.TenantId]: gqlState.tenantId,
    },
  };
});

const errorRefreshLink = new ErrorLink(({ operation, forward, error }) => {
  const netStatus = (error as any)?.status ?? (error as any)?.statusCode ?? (error as any)?.response?.status ?? (error as any)?.result?.status;
  const net401 = netStatus === 401;
  if (!net401) return forward(operation);

  return new Observable((observer) => {
    let sub: any;
    const { refreshAttempted } = operation.getContext();
    if (refreshAttempted) return forward(operation).subscribe(observer);
    (async () => {
      try {
        await gqlState.getRefreshedToken();
        operation.setContext({ ...operation.getContext(), refreshAttempted: true });
        sub = forward(operation).subscribe(observer);
      } catch (e) {
        observer.error(e);
      }
    })();
    return () => sub?.unsubscribe();
  });
});

const gqlClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorRefreshLink.concat(apolloAuthLink).concat(apolloHttpLink),
  devtools: {
    enabled: !!import.meta.hot,
  },
});

const SafePostHog = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
  ? ({ children }: { children: ReactElement }) => (
      <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={postHogOptions}>
        {children}
      </PostHogProvider>
    )
  : Fragment;

const IndexWrapped: FC = () => (
  <DebugHotkeyProvider>
    <I18nextProvider i18n={i18n}>
      <ToastProvider>
        <SafePostHog>
          <QueryClientProvider client={queryClient}>
            <ApolloProvider client={gqlClient}>
              <ReactQueryDevtools initialIsOpen={false} position="bottom" />
              <ImageCacheProvider>
                <LexicalIntegrationsRoot>
                  <BannerProvider>
                    <LayoutProvider>
                      <RouterProvider router={router} />
                    </LayoutProvider>
                  </BannerProvider>
                </LexicalIntegrationsRoot>
              </ImageCacheProvider>
            </ApolloProvider>
          </QueryClientProvider>
        </SafePostHog>
      </ToastProvider>
    </I18nextProvider>
  </DebugHotkeyProvider>
);

export default IndexWrapped;
